<template>
  <div class="admin-login">
    <v-app-bar elevation="0" class="login-app-bar">
      <v-img alt="On the Clock Now" class="login-logo" contain @click="$router.go(0)"
        src="../../../assets/fullLogo.png" transition="scale-transition" width="150" height="42"
        max-width="150" />
      <v-spacer></v-spacer>
      <span class="login-version">v{{ version }}</span>
      <v-menu offset-y content-class="login-menu">
        <template v-slot:activator="{ on, attrs }">
          <v-btn class="login-menu-button" icon v-bind="attrs" v-on="on" aria-label="Login options">
            <v-icon>mdi-dots-vertical</v-icon>
          </v-btn>
        </template>
        <v-list>
          <v-list-item @click.stop.prevent="switchLocation()">
            <v-list-item-icon><v-icon>mdi-map-marker-outline</v-icon></v-list-item-icon>
            <v-list-item-title>Switch location</v-list-item-title>
          </v-list-item>
          <v-list-item @click.stop.prevent="addTimeClockToken()">
            <v-list-item-icon><v-icon>mdi-clock-outline</v-icon></v-list-item-icon>
            <v-list-item-title>Employee time clock</v-list-item-title>
          </v-list-item>
          <v-divider></v-divider>
          <v-list-item @click.stop.prevent="$router.push({ path: '/backoffice/login' })">
            <v-list-item-icon><v-icon>mdi-account-outline</v-icon></v-list-item-icon>
            <v-list-item-title>Account login</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
    </v-app-bar>

    <main class="login-main">
      <section class="login-card">
        <header class="login-heading">
          <span class="login-heading-icon"><v-icon>mdi-shield-account-outline</v-icon></span>
          <div>
            <small>Admin access</small>
            <h1>Welcome back</h1>
            <p v-if="location"><v-icon small>mdi-map-marker-outline</v-icon>{{ location.name }}</p>
          </div>
        </header>

        <CSPLoader v-if="loading" />

        <div class="login-form">
          <label class="field-label">Who is signing in?</label>
          <v-select class="employee-select" hide-details="auto" label="Select administrator"
            :items="getTimeClockUsers.admins" v-model="selectedEmployee" item-text="name" return-object outlined
            :error-messages="selectedEmployeeErrors" @input="$v.selectedEmployee.$touch()"
            :loading="employees.length === 0" prepend-inner-icon="mdi-account-outline"></v-select>

          <div class="login-method" role="tablist" aria-label="Login method">
            <button type="button" :class="{ active: !pinView }" @click="pinView = false">
              <v-icon small>mdi-dialpad</v-icon>PIN
            </button>
            <button type="button" :class="{ active: pinView }" @click="pinView = true">
              <v-icon small>mdi-form-textbox-password</v-icon>Password
            </button>
          </div>

          <div v-if="pinView" class="password-login">
            <label class="field-label">Enter your password</label>
            <v-text-field outlined v-model="password"
              :append-icon="showPassword ? 'mdi-eye-off-outline' : 'mdi-eye-outline'"
              :type="showPassword ? 'text' : 'password'" label="Password"
              prepend-inner-icon="mdi-lock-outline" @click:append="showPassword = !showPassword"
              @keyup.enter="selectedEmployee && password && submit()"></v-text-field>
            <v-btn class="password-submit" color="primary" rounded large block
              :disabled="!selectedEmployee || !password" :loading="loading" @click="submit">
              Sign in<v-icon right>mdi-arrow-right</v-icon>
            </v-btn>
          </div>

          <div v-else class="pin-login">
            <div class="pin-instruction">
              <span>Enter your 4-digit PIN</span>
              <small v-if="!selectedEmployee">Select your name first</small>
            </div>
            <div class="pin-dots" aria-label="PIN entry">
              <span v-for="circle in pinCodeCircles" :key="circle" :class="{ filled: pin.length >= circle }"></span>
            </div>

            <div class="pin-keypad">
              <button v-for="num in numbers" :key="num" type="button" :disabled="!selectedEmployee"
                @pointerdown.stop.prevent="pointerMode && selectedNum(num)"
                @click.stop.prevent="!pointerMode && selectedNum(num)">{{ num }}</button>
              <button type="button" class="pin-action" :disabled="!selectedEmployee || !pin"
                @pointerdown.stop.prevent="pointerMode && clearPin()"
                @click.stop.prevent="!pointerMode && clearPin()" aria-label="Clear PIN">
                <v-icon>mdi-close-circle-outline</v-icon><small>Clear</small>
              </button>
              <button type="button" :disabled="!selectedEmployee"
                @pointerdown.stop.prevent="pointerMode && selectedNum(0)"
                @click.stop.prevent="!pointerMode && selectedNum(0)">0</button>
              <button type="button" class="pin-action" :disabled="!selectedEmployee || !pin"
                @pointerdown.stop.prevent="pointerMode && deletePin()"
                @click.stop.prevent="!pointerMode && deletePin()" aria-label="Delete last PIN digit">
                <v-icon>mdi-backspace-outline</v-icon><small>Delete</small>
              </button>
            </div>
            <p class="pin-help"><v-icon small>mdi-information-outline</v-icon>Login continues automatically after four digits.</p>
          </div>
        </div>
      </section>
    </main>
  </div>
</template>

<script>
import employeeService from "@/modules/Employee/service.js";
// import locationService from "@/modules/Location/service.js"
// import LoginPad from "@/components/LoginPad.vue";
import authService from "@/modules/Authentication/service.js";
import { mapActions, mapGetters } from "vuex";
const { validationMixin } = require("vuelidate");
const { required } = require("vuelidate/lib/validators");
const pjson = require("../../../../package.json");

export default {
  data() {
    return {
      password: null,
      showPassword: false,
      pinView: false,
      pointerMode: "PointerEvent" in window,
      pin: "",
      selectedEmployee: null,
      numbers: [1, 2, 3, 4, 5, 6, 7, 8, 9],
      pinCodeCircles: [1, 2, 3, 4],
      loading: false,
      taostText: "",
      pinCode: null,
      selectedEmployee: null,
      employees: [],
      location: null,
      locationCode: null,
      deviceCode: null,
      deviceName: null,
    };
  },
  watch: {
    pin() {
      // Reseting Pin
      if (this.pin.length === 5) {
        this.pin = "";
      }

      // Submiting
      if (this.pin.length === 4) {
        this.pinSubmit(this.pin);
      }
    },
    // selectedEmployee() {
    //   if (this.selectedEmployee && this.selectedEmployee.id) {
    //     this.$emit("employeeSubmit", this.selectedEmployee);
    //   }
    // },
  },
  async created() {
    localStorage.removeItem(`${process.env.VUE_APP_STORAGE_PREFIX}backofficeToken`);
    localStorage.setItem(
      `${process.env.VUE_APP_STORAGE_PREFIX}selectedScreen`,
      "Admin Panel"
    );
    if (
      localStorage.getItem(`${process.env.VUE_APP_STORAGE_PREFIX}code`) != null
    ) {
      this.locationCode = localStorage.getItem(
        `${process.env.VUE_APP_STORAGE_PREFIX}code`
      );
    } else if (this.$route.params && this.$route.params.locationInstance) {
      const locationInstance = this.$route.params.locationInstance;
      this.locationCode = locationInstance.code;
      this.deviceName = locationInstance.deviceName;
    } else {
      this.$router.push({ name: "authentication-find-location" });
    }

    this.location = JSON.parse(
      localStorage.getItem(`${process.env.VUE_APP_STORAGE_PREFIX}location`)
    );

    if (this.locationCode) {
      await authService
        .getLocation(this.locationCode)
        .then(async (response) => {
          this.location = response.data;
          this.locationCode = response.data.code;
          // console.log("emplyee list", response);
          this.employees = response.data.Employees;
          if (this.employees) {
            this.setEmployeeList(this.employees);
          }
          await this.filterAdmins;
        })
        .catch((error) => {
          this.$router.push({ name: "authentication-find-location" });
          console.log(error);
        });
      // console.log(
      //   "lad emplyee",
      //   localStorage.getItem(
      //     `${process.env.VUE_APP_STORAGE_PREFIX}lastEmployeeId`
      //   )
      // );
      if (
        this.getTimeClockUsers.admins &&
        this.getTimeClockUsers.admins.length === 1
      ) {
        this.selectedEmployee = this.getTimeClockUsers.admins[0];
      } else {
        let lastloginId = localStorage.getItem(
          `${process.env.VUE_APP_STORAGE_PREFIX}lastEmployeeId`
        );
        if (lastloginId) {
          this.getTimeClockUsers.admins.map((user) => {
            if (user.id === parseInt(lastloginId)) {
              this.selectedEmployee = user;
            }
          });
        }
      }
    } else {
      this.$router.push({ name: "authentication-find-location" });
    }

    const { deviceName, deviceCode } = authService.getStoredDevice();
    this.deviceName = deviceName;
    this.deviceCode = deviceCode;
    if (
      !localStorage.getItem(`${process.env.VUE_APP_STORAGE_PREFIX}deviceCode`)
    ) {
      localStorage.setItem(
        `${process.env.VUE_APP_STORAGE_PREFIX}deviceCode`,
        this.deviceCode
      );
    }
    // if (!this.deviceName) {
    //   this.deviceName = "Phone";
    // }

    console.log("deviceName", this.locationCode, this.deviceCode, deviceName);
  },
  computed: {
    ...mapGetters("global", ["getTimeClockUsers"]),
    version() {
      // console.log('pjson', pjson.version)
      return pjson.version;
    },
    selectedEmployeeErrors() {
      const errors = [];
      if (!this.$v.selectedEmployee.$dirty) return errors;
      if (!this.$v.selectedEmployee.required)
        errors.push("Employee is required.");
      return errors;
    },
    generateTimeClockToken() {
      let rand = function () {
        return Math.random().toString(36).substr(2);
      };

      return rand() + rand();
    },
    filterAdmins() {
      const admins = [];
      const employees = [];
      this.employees.map((user) => {
        if (user.metadata && user.metadata.timeClockLogin) {
          // sorting out admins
          if (user.metadata.timeClockLogin.admin) {
            admins.push(user);
          }

          // soring out employees
          if (user.metadata.timeClockLogin.employee) {
            employees.push(user);
          }
        }
      });

      this.employees.map((user, index) => {
        // if no one specified as admin then user with 0 index will be admin
        if (admins.length === 0) {
          if (index === 0) {
            if (user.metadata && !user.metadata.timeClockLogin) {
              user.metadata.timeClockLogin = {
                admin: true,
                employee: false,
                updateMetaData: true,
              };
              admins.push(user);
            }

            if (
              user.metadata &&
              user.metadata.timeClockLogin &&
              !user.metadata.timeClockLogin.admin &&
              !user.metadata.timeClockLogin.employee
            ) {
              user.metadata.timeClockLogin.admin = true;
              (user.metadata.timeClockLogin.updateMetaData = true),
                admins.push(user);
            }

            if (user && !user.metadata) {
              user.metadata = {
                timeClockLogin: {
                  admin: true,
                  employee: false,
                  updateMetaData: true,
                },
              };
              admins.push(user);
            }
          }
        }
      });

      // adding in store
      this.setTimeClockAdmins(admins);
      this.setTimeClockEmployees(employees);
    },
  },
  mixins: [validationMixin],
  validations: {
    selectedEmployee: {
      required,
    },
  },
  methods: {
    ...mapActions("employee", ["setEmployee"]),
    ...mapActions("global", [
      "setEmployeeList",
      "setTimeClockAdmins",
      "setTimeClockEmployees",
    ]),
    pinColor(num) {
      if (this.pin.length >= num) {
        return "secondary";
      }
      else {
        return "grey";
      }
    },
    pinCodeStyle(num) {
      if (this.pin.length >= num) {
        return "mdi-check-circle";
      }
      else {
        return "mdi-checkbox-blank-circle-outline";
      }
    },
    selectedNum(num) {
      num = num.toString();
      this.pin = this.pin.concat(num).trim();
    },
    clearPin() {
      this.pin = ""
    },
    deletePin() {
      this.pin = this.pin.slice(0, -1);
    },
    switchLocation() {
      return this.$swal
        .fire({
          title: "Are you sure?",
          text: "You want to change location account",
          icon: "warning",
          showCancelButton: true,
          confirmButtonColor: "#3085d6",
          cancelButtonColor: "#d33",
          confirmButtonText: "Yes",
          cancelButtonText: "No",
        })
        .then((result) => {
          if (result.isConfirmed) {
            this.$router.push({ name: "authentication-find-location" });
            // this.$router.push({ path: "/login/location" });
          }
        })
        .catch((error) => {
          console.log(error);
        });
    },
    addTimeClockToken() {
      localStorage.setItem(
        `${process.env.VUE_APP_STORAGE_PREFIX}selectedScreen`,
        "Time Clock"
      );
      //await localStorage.setItem(`${process.env.VUE_APP_STORAGE_PREFIX}TimeClocktoken`, this.generateTimeClockToken);
      this.$router.push({ name: "clockInOut" });
    },
    async updateEmployee(id, empdata) {
      const admins = this.getTimeClockUsers.admins;
      // adding it to update state.
      let change = false;

      admins.map(async (user) => {
        if (
          user.metadata &&
          user.metadata.timeClockLogin &&
          user.metadata.timeClockLogin.updateMetaData
        ) {
          delete user.metadata.timeClockLogin.updateMetaData;

          // making it true to update to state.
          change = true;

          await employeeService
            .update(user.id, user)
            .then((response) => response)
            .catch((error) => {
              console.log(error);
              throw error;
            });

          return false;
        }
      });

      if (change) {
        this.setTimeClockAdmins(admins);
        change = false;
      }

      if (id && empdata) {
        return employeeService
          .update(id, empdata)
          .then((response) => response)
          .catch((error) => {
            console.log(error);
            throw error;
          });
      }
    },
    pinSubmit(val) {
      this.pinCode = val;
      this.submit();
    },
    // employeeSubmit(emp) {
    //   console.log('this.selectedEmployeethis.selectedEmployeethis.selectedEmployeethis.selectedEmployeethis.selectedEmployeethis.selectedEmployee')
    //   if (emp) {
    //     this.selectedEmployee = emp;
    //     this.setEmployee(emp);
    //   }
    // },
    setRecentLocation() {

      let recentLocation = JSON.parse(
        localStorage.getItem(
          `${process.env.VUE_APP_STORAGE_PREFIX}recentLocation`
        )
      );
      // console.log("fdffffffffffffff", recentLocation);
      if (recentLocation) {
        recentLocation.map((row) => {
          if (row.code === this.locationCode) {
            if (this.pin) {
              row.pin = this.pin;
              row.employeeId = this.selectedEmployee.id;
            }
            if (this.password) {
              row.pin = this.password;
              row.employeeId = this.selectedEmployee.id;
            }
          }
        });
        localStorage.setItem(
          `${process.env.VUE_APP_STORAGE_PREFIX}recentLocation`,
          JSON.stringify(recentLocation)
        );
      }
    },
    submit() {
      this.$Progress.start();
      this.loading = true;
      const loginInfo = {
        id: this.selectedEmployee.id,
        pin: this.pinView ? this.password : this.pinCode,
        locationCode: this.locationCode,
        deviceCode: this.deviceCode,
        deviceName: this.deviceName,
      };
      authService
        .authenticate(loginInfo)
        .then(async (res) => {
          console.log("res", res);
          this.loading = false;
          // await localStorage.setItem(
          //   `${process.env.VUE_APP_STORAGE_PREFIX}Hometoken`,
          //   this.generateTimeClockToken
          // );
          authService.setLastEmployeeId(this.selectedEmployee.id);

          this.setEmployee(this.selectedEmployee);
          this.setRecentLocation();
          // await this.updateEmployee();
          console.log("going home");
          this.$router.push({ name: "home" });
          console.log("going home done");
        })
        .catch((error) => {
          console.log("error in login", error);
          this.loading = false;
          this.$Progress.fail();
          this.$toast.error(error.data ? error.data.message : "Error", {
            timeout: 2000,
          });

          this.pin = "";
          // this.taostText = "Invalid login";
          // this.snackbar = true;
        });
    },
  },
};
</script>

<style scoped>
.admin-login {
  background:
    radial-gradient(circle at 72% 12%, rgba(37, 99, 235, 0.12), transparent 34%),
    var(--v-background-base);
  color: var(--v-textPrimary-base);
  min-height: 100vh;
}

.login-app-bar {
  background: var(--v-surface-base) !important;
  border-bottom: 1px solid var(--v-border-base) !important;
}

.login-logo {
  cursor: pointer;
  flex: 0 0 150px;
  height: 42px !important;
  max-height: 42px;
  max-width: 150px !important;
  width: 150px !important;
}

.login-logo ::v-deep .v-responsive__sizer {
  padding-bottom: 0 !important;
}

.login-version {
  color: var(--v-textSecondary-base);
  font-size: 0.65rem;
  margin-right: 4px;
}

.login-menu-button {
  color: var(--v-textPrimary-base) !important;
}

.login-main {
  align-items: flex-start;
  display: flex;
  justify-content: center;
  padding: 32px 16px 48px;
}

.login-card {
  background: var(--v-surface-base);
  border: 1px solid var(--v-border-base);
  border-radius: 24px;
  box-shadow: 0 22px 60px rgba(4, 15, 30, 0.14);
  max-width: 520px;
  overflow: hidden;
  width: 100%;
}

.login-heading {
  align-items: center;
  border-bottom: 1px solid var(--v-border-base);
  display: flex;
  gap: 14px;
  padding: 22px 24px 18px;
}

.login-heading-icon {
  align-items: center;
  background: rgba(96, 165, 250, 0.13);
  border: 1px solid rgba(96, 165, 250, 0.24);
  border-radius: 15px;
  display: flex;
  flex: 0 0 50px;
  height: 50px;
  justify-content: center;
}

.login-heading-icon .v-icon {
  color: var(--v-primary-base);
}

.login-heading small {
  color: var(--v-primary-base);
  font-size: 0.65rem;
  font-weight: 800;
  letter-spacing: 0.11em;
  text-transform: uppercase;
}

.login-heading h1 {
  color: var(--v-textPrimary-base);
  font-size: 1.45rem;
  margin: 1px 0 2px;
}

.login-heading p {
  align-items: center;
  color: var(--v-textSecondary-base);
  display: flex;
  font-size: 0.75rem;
  gap: 4px;
  margin: 0;
}

.login-heading p .v-icon {
  color: var(--v-textSecondary-base);
}

.login-form {
  padding: 22px 24px 24px;
}

.field-label {
  color: var(--v-textPrimary-base);
  display: block;
  font-size: 0.72rem;
  font-weight: 750;
  margin-bottom: 7px;
}

.employee-select ::v-deep .v-input__slot,
.password-login ::v-deep .v-input__slot {
  background: var(--v-surfaceMuted-base) !important;
}

.login-method {
  background: var(--v-surfaceMuted-base);
  border: 1px solid var(--v-border-base);
  border-radius: 13px;
  display: grid;
  gap: 4px;
  grid-template-columns: 1fr 1fr;
  margin: 18px 0;
  padding: 4px;
}

.login-method button {
  align-items: center;
  background: transparent;
  border: 0;
  border-radius: 10px;
  color: var(--v-textSecondary-base);
  cursor: pointer;
  display: flex;
  font: inherit;
  font-size: 0.78rem;
  font-weight: 750;
  gap: 6px;
  justify-content: center;
  padding: 10px;
}

.login-method button .v-icon {
  color: inherit;
}

.login-method button.active {
  background: var(--v-primary-base);
  color: #fff;
  box-shadow: 0 5px 14px rgba(37, 99, 235, 0.22);
}

.password-login {
  padding-top: 2px;
}

.password-submit {
  font-weight: 750;
  letter-spacing: 0;
  text-transform: none;
}

.pin-instruction {
  align-items: center;
  display: flex;
  justify-content: space-between;
  margin-bottom: 11px;
}

.pin-instruction span {
  color: var(--v-textPrimary-base);
  font-size: 0.78rem;
  font-weight: 750;
}

.pin-instruction small {
  color: #f0aa45;
  font-size: 0.65rem;
}

.pin-dots {
  display: flex;
  gap: 12px;
  justify-content: center;
  margin: 6px 0 18px;
}

.pin-dots span {
  background: transparent;
  border: 2px solid var(--v-border-base);
  border-radius: 50%;
  height: 18px;
  transition: 120ms ease;
  width: 18px;
}

.pin-dots span.filled {
  background: var(--v-primary-base);
  border-color: var(--v-primary-base);
  box-shadow: 0 0 0 4px rgba(96, 165, 250, 0.12);
}

.pin-keypad {
  display: grid;
  gap: 9px;
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.pin-keypad button {
  align-items: center;
  background: var(--v-surfaceMuted-base);
  border: 1px solid var(--v-border-base);
  border-radius: 14px;
  color: var(--v-textPrimary-base);
  cursor: pointer;
  display: flex;
  font: inherit;
  font-size: 1.55rem;
  font-weight: 750;
  height: 66px;
  justify-content: center;
  touch-action: manipulation;
  transition: transform 80ms ease, border-color 120ms ease;
}

.pin-keypad button:not(:disabled):active {
  border-color: var(--v-primary-base);
  transform: scale(0.97);
}

.pin-keypad button:disabled {
  cursor: not-allowed;
  opacity: 0.42;
}

.pin-keypad .pin-action {
  color: var(--v-textSecondary-base);
  flex-direction: column;
  font-size: 0.65rem;
  gap: 2px;
}

.pin-keypad .pin-action .v-icon {
  color: inherit;
}

.pin-keypad .pin-action:first-of-type {
  color: #f36b75;
}

.pin-help {
  align-items: center;
  color: var(--v-textSecondary-base);
  display: flex;
  font-size: 0.66rem;
  gap: 5px;
  justify-content: center;
  margin: 14px 0 0;
}

.pin-help .v-icon {
  color: var(--v-textSecondary-base);
}

@media (max-width: 600px) {
  .login-app-bar {
    height: 58px !important;
  }

  .login-logo {
    flex-basis: 128px;
    height: 36px !important;
    max-height: 36px;
    max-width: 128px !important;
    width: 128px !important;
  }

  .login-main {
    padding: 14px 10px 24px;
  }

  .login-card {
    border-radius: 18px;
  }

  .login-heading {
    padding: 16px;
  }

  .login-heading-icon {
    flex-basis: 44px;
    height: 44px;
  }

  .login-heading h1 {
    font-size: 1.22rem;
  }

  .login-form {
    padding: 16px;
  }

  .login-method {
    margin: 14px 0;
  }

  .pin-keypad {
    gap: 8px;
  }

  .pin-keypad button {
    height: min(64px, 9vh);
  }
}
</style>
